import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { leaguesAPI } from '../../../../services/apiService';

export const useMyTeam = (draftState, leagueId) => {
  const [myTeam, setMyTeam] = useState(null);
  const [isCommissioner, setIsCommissioner] = useState(false);
  const [teamsMap, setTeamsMap] = useState(new Map());
  const [cashBalance, setCashBalance] = useState(null);
  const [keptPlayers, setKeptPlayers] = useState([]);
  const teamIdRef = useRef(null);

  // Fetch team data on mount
  useEffect(() => {
    const fetchTeamData = async () => {
      if (!leagueId) return;

      try {
        const response = await leaguesAPI.getLiveDraftConnection(leagueId);
        if (response.user_team) {
          setMyTeam(response.user_team);
          setIsCommissioner(response.is_commissioner || false);
          teamIdRef.current = response.user_team.team_id;

          // Fetch cash balance from ledger
          const ledgerResponse = await leaguesAPI.getTeamCashLedger(leagueId, response.user_team.team_id, 1);
          if (ledgerResponse.success && ledgerResponse.current_balance !== undefined) {
            setCashBalance(ledgerResponse.current_balance);
          }
        }

        // Also fetch all teams for mapping
        const teamsResponse = await leaguesAPI.getLeagueTeams(leagueId);
        if (teamsResponse.success && teamsResponse.teams) {
          const map = new Map();
          teamsResponse.teams.forEach(team => {
            map.set(team.team_id, team);
          });
          setTeamsMap(map);
        }
      } catch (err) {
        console.error('Error fetching team data:', err);
      }
    };

    fetchTeamData();
  }, [leagueId]);

  // Fetch pre-draft roster (kept players tradeable during draft)
  useEffect(() => {
    if (!leagueId) return;
    const fetchKeptPlayers = async () => {
      try {
        const response = await leaguesAPI.getMyRosterCanonical(leagueId);
        setKeptPlayers(response?.players || []);
      } catch (err) {
        console.error('[useMyTeam] Error fetching kept players:', err);
      }
    };
    fetchKeptPlayers();
  }, [leagueId]);

  // Refresh cash balance when picks change (when a pick is made)
  const picksMadeCount = draftState?.picksMade || 0;
  useEffect(() => {
    const refreshCashBalance = async () => {
      if (!leagueId || !teamIdRef.current) return;

      try {
        const ledgerResponse = await leaguesAPI.getTeamCashLedger(leagueId, teamIdRef.current, 1);
        if (ledgerResponse.success && ledgerResponse.current_balance !== undefined) {
          setCashBalance(ledgerResponse.current_balance);
        }
      } catch (err) {
        console.error('Error refreshing cash balance:', err);
      }
    };

    if (picksMadeCount > 0) {
      refreshCashBalance();
    }
  }, [leagueId, picksMadeCount]);

  // Computed: My picks from draft state
  const myPicks = useMemo(() => {
    if (!draftState?.picks || !myTeam) return [];
    return draftState.picks.filter(p =>
      p.current_owner_team_id === myTeam.team_id
    );
  }, [draftState?.picks, myTeam]);

  // Computed: My roster (players I've drafted)
  const myRoster = useMemo(() => {
    return myPicks
      .filter(pick => pick.player_selected || pick.league_player_id)
      .map(pick => pick.player_selected || {
        ids: { league_player: pick.league_player_id },
        info: {
          full_name: pick.player?.info?.full_name,
          position: pick.player?.info?.position
        }
      });
  }, [myPicks]);

  // Computed: Is it my turn?
  const isMyTurn = useMemo(() => {
    if (!draftState?.currentPick || !myTeam) return false;
    if (draftState.status !== 'in_progress') return false;
    if (draftState.paused) return false;
    return draftState.currentPick.current_owner_team_id === myTeam.team_id;
  }, [draftState, myTeam]);

  // Computed: Next pick number for my team
  const nextPickNumber = useMemo(() => {
    if (!draftState?.picks || !myTeam) return null;

    const myUpcomingPicks = draftState.picks.filter(p =>
      p.current_owner_team_id === myTeam.team_id &&
      !p.player_selected &&
      !p.league_player_id
    );

    if (myUpcomingPicks.length === 0) return null;

    return Math.min(...myUpcomingPicks.map(p => p.overall_pick_number));
  }, [draftState?.picks, myTeam]);

  // Pick count
  const pickCount = useMemo(() => myRoster.length, [myRoster]);

  // Get team by ID helper
  const getTeamById = useCallback((teamId) => {
    return teamsMap.get(teamId);
  }, [teamsMap]);

  return {
    myTeam,
    setMyTeam,
    isCommissioner,
    setIsCommissioner,
    teamsMap,
    setTeamsMap,
    getTeamById,
    myPicks,
    myRoster,
    isMyTurn,
    nextPickNumber,
    pickCount,
    cashBalance,
    keptPlayers
  };
};
